import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { Auth } from '@aws-amplify/auth';
import { Store } from '@ngrx/store';
import { Observable, tap } from 'rxjs';
import { CanComponentDeactivate } from 'src/app/shared/guards/can-deactivate.guard';
import { ApiErrorResponse } from 'src/app/shared/interfaces/api-error-response';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { SubSink } from 'subsink';

import { UserResponseModel } from '@app/api/models';
import {
  MerchantAccountActions,
  MerchantAccountSelectors,
} from '@app/merchant-account/store/merchant-account.slice';
import {
  getServerErrorForField,
  processErrorResponse,
} from '@shared/helpers/form-helper';
import { trimInput } from '@shared/helpers/input-trim-helper';
import { isPhoneNumber } from '@shared/validators/phone-number.validator';

@Component({
  selector: 'app-user-edit',
  templateUrl: './user-edit.component.html',
  styleUrls: ['./user-edit.component.scss'],
})
export class UserEditComponent
  implements OnDestroy, OnInit, CanComponentDeactivate
{
  userId: string = '';

  user$: Observable<UserResponseModel | undefined> = this.store.select(
    MerchantAccountSelectors.selectActiveUser
  );

  activeUserLoading$: Observable<boolean> = this.store.select(
    MerchantAccountSelectors.selectActiveUserLoading
  );

  activeUserError$: Observable<boolean> = this.store.select(
    MerchantAccountSelectors.selectActiveUserError
  );

  errorResponse$: Observable<ApiErrorResponse | undefined> = this.store.select(
    MerchantAccountSelectors.selectEditMerchantAdminErrorResponse
  );

  private subs = new SubSink();

  isUserAdminToggleDisabled: boolean = false;

  form: UntypedFormGroup = this.fb.group({
    email: new UntypedFormControl(null, []),
    phoneNumber: new UntypedFormControl(null, [
      Validators.required,
      isPhoneNumber,
    ]),
    lastName: new UntypedFormControl(null, [Validators.required]),
    firstName: new UntypedFormControl(null, [Validators.required]),
    roles: new UntypedFormControl(null, [Validators.required]),
  });

  roleList: string[] = [
    'PRODUCT_ADMIN',
    'PRICING_ADMIN',
    'MERCHANT_ADMIN',
    'COMMERCIAL_ADMIN',
    'EXCISE_MANAGER',
    'SUBSCRIPTION_ADMIN',
    'SETTINGS_ADMIN',
    'USER_ADMIN',
    'SUPPLY_MANAGER',
  ];

  async ngOnInit(): Promise<void> {
    this.subs.sink = this.route.params.subscribe((params) => {
      if (params['id']) {
        this.userId = params['id'];
        this.store.dispatch(
          MerchantAccountActions.getUser.trigger({ userId: params['id'] })
        );
      }
    });
    this.subs.sink = this.user$.subscribe((user) => {
      if (user) {
        this.emailControl.setValue(user.email);
        this.phoneNumberControl.setValue(user.phoneNumber);
        this.firstNameControl.setValue(user.firstName);
        this.lastNameControl.setValue(user.lastName);
        this.rolesControl.setValue(user.roles);

        Auth.currentSession().then((user) => {
          if (user.getIdToken().payload['sub'] === this.userId) {
            this.isUserAdminToggleDisabled = true;
          }
        });
      }
    });

    this.subs.sink = this.errorResponse$.subscribe((response) =>
      processErrorResponse(this.form, response)
    );
  }

  constructor(
    private store: Store,
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private readonly dialogService: DialogService
  ) {}

  onSubmit() {
    if (this.form.valid) {
      this.form.markAsPristine();
      this.store.dispatch(
        MerchantAccountActions.editMerchantAdmin.trigger({
          userId: this.userId,
          request: trimInput(this.form.value),
        })
      );
    }
  }

  getServerError(fieldName: string): any {
    return getServerErrorForField(this.form, fieldName);
  }

  isRoleDisabled(role: string): boolean {
    if (role === 'USER_ADMIN') {
      return this.isUserAdminToggleDisabled;
    } else return false;
  }

  get emailControl(): UntypedFormControl {
    return this.form.get('email') as UntypedFormControl;
  }
  get phoneNumberControl(): UntypedFormControl {
    return this.form.get('phoneNumber') as UntypedFormControl;
  }
  get firstNameControl(): UntypedFormControl {
    return this.form.get('firstName') as UntypedFormControl;
  }
  get lastNameControl(): UntypedFormControl {
    return this.form.get('lastName') as UntypedFormControl;
  }
  get rolesControl(): UntypedFormControl {
    return this.form.get('roles') as UntypedFormControl;
  }

  canDeactivate() {
    return this.form.dirty ? this.dialogService.confirmUnsavedChanges() : true;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
